/**
 * /dashboard/accounting — Accounting Overview hub.
 *
 * Pattern E hub landing. The shared AccountingModuleShell renders the
 * horizontal ModuleNav strip (Overview · Sales · Purchases · Money ·
 * Contacts · Items · Reports · Period locks · Audit) — the strip
 * already advertises every destination, so this page leans into the
 * tiles for newcomers landing from the main sidebar.
 *
 * Tiles are static (no API call) — the per-section pages already do
 * their own data fetching. A future iteration can surface tenant-level
 * KPIs here (open invoices, overdue MSME bills, period status) once we
 * have a /accounting/overview aggregate endpoint.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/page.tsx with the
 * old Pattern-E `<ModuleShell>` rail folded into AccountingModuleShell.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  ClipboardList,
  FileBarChart,
  FileClock,
  History,
  Package,
  Receipt,
  ShieldCheck,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/accounting/")({
  component: AccountingOverviewRoute,
});

interface Tile {
  label: string;
  href: string;
  icon: LucideIcon;
  blurb: string;
}

const TILES: readonly Tile[] = [
  {
    label: "Sales",
    href: "/app/$tenantSlug/accounting/sales",
    icon: Receipt,
    blurb: "Invoices and quotes for your customers.",
  },
  {
    label: "Purchases",
    href: "/app/$tenantSlug/accounting/purchases",
    icon: ClipboardList,
    blurb: "Vendor bills with MSME-overdue tracking.",
  },
  {
    label: "Money",
    href: "/app/$tenantSlug/accounting/money",
    icon: Wallet,
    blurb: "Payment accounts, receipts and payments.",
  },
  {
    label: "Contacts",
    href: "/app/$tenantSlug/accounting/contacts",
    icon: Users,
    blurb: "Customer, vendor, employee and other contacts.",
  },
  {
    label: "Items",
    href: "/app/$tenantSlug/accounting/items",
    icon: Package,
    blurb: "Goods and services with HSN/SAC and default tax.",
  },
  {
    label: "Reports",
    href: "/app/$tenantSlug/accounting/reports",
    icon: FileBarChart,
    blurb: "Trial balance, P&L, balance sheet, MSME overdue.",
  },
  {
    label: "Tax Returns",
    href: "/app/$tenantSlug/accounting/returns",
    icon: FileClock,
    blurb: "GST (GSTR-1, GSTR-3B) and TDS (24Q, Form 16) filing snapshots.",
  },
  {
    label: "Period Locks",
    href: "/app/$tenantSlug/accounting/period-locks",
    icon: ShieldCheck,
    blurb: "Year-end and monthly close. Unlock needs override capability.",
  },
  {
    label: "Audit Trail",
    href: "/app/$tenantSlug/accounting/audit",
    icon: History,
    blurb: "Books-of-account audit events (Rule 3(1)).",
  },
] as const;

function AccountingOverviewRoute() {
  return (
    <AccountingModuleShell
      title="Overview"
      description="Sales, purchases, money, master data and reports — at a glance."
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <Link key={t.href} to={t.href} className="group">
              <Card className="h-full transition-colors group-hover:border-primary/40">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                      <Icon className="h-5 w-5" />
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </div>
                  <CardTitle className="mt-3 text-base">{t.label}</CardTitle>
                  <CardDescription>{t.blurb}</CardDescription>
                </CardHeader>
              </Card>
            </Link>
          );
        })}
      </div>
    </AccountingModuleShell>
  );
}
